import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Chip, LinearProgress, Snackbar } from '@mui/material';
import { collection, onSnapshot } from 'firebase/firestore';

import { db } from '../../firebase.js';
import { useUser } from '../../contexts/UserContext.js';
import { WASTE_RECEIVERS_COLLECTION } from '../../common/wasteReceivers.js';
import { normalizeTransitionWasteReceiverDoc } from '../../common/wasteReceiverSortingListTransition.js';
import {
    loadCompanyOptions,
    normalizeCompanyMembershipPermissionEntry,
    normalizeUserDoc,
    SETUP_ENTITY_TYPES,
} from './setupShared.js';
import useWasteReceiverTab from './WasteReceiverTab/useWasteReceiverTab.js';
import WasteReceiverPanel from './WasteReceiverTab/WasteReceiverPanel.jsx';
import WasteReceiverToolbarActions from './WasteReceiverTab/WasteReceiverToolbarActions.jsx';
import WasteReceiverDialogs from './WasteReceiverTab/WasteReceiverDialogs.jsx';
import useCompaniesTab from './CompaniesTab/useCompaniesTab.js';
import CompaniesPanel from './CompaniesTab/CompaniesPanel.jsx';
import CompaniesToolbarActions from './CompaniesTab/CompaniesToolbarActions.jsx';
import useUsersTab from './UsersTab/useUsersTab.js';
import UsersPanel from './UsersTab/UsersPanel.jsx';
import UserEditorDialog from './UsersTab/UserEditorDialog.jsx';
import UsersToolbarActions from './UsersTab/UsersToolbarActions.jsx';
import CreateUsersDialog from './UsersTab/CreateUsersDialog.jsx';
import WelcomeEmailTemplatesDialog from './UsersTab/WelcomeEmailTemplatesDialog.jsx';
import SendWelcomeEmailDialog from './UsersTab/SendWelcomeEmailDialog.jsx';
import './SetupPage.css';

const SetupPage = () => {
    const { userData } = useUser();

    const [wasteReceivers, setWasteReceivers] = useState([]);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [setupEntityType, setSetupEntityType] = useState(SETUP_ENTITY_TYPES.WASTE_RECEIVER);
    const [loadingReceivers, setLoadingReceivers] = useState(true);
    const [loadingCompanies, setLoadingCompanies] = useState(true);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [saving, setSaving] = useState(false);
    const [receiverLoadError, setReceiverLoadError] = useState('');
    const [companyLoadError, setCompanyLoadError] = useState('');
    const [userLoadError, setUserLoadError] = useState('');
    const [snackbar, setSnackbar] = useState(null);

    const companyById = useMemo(() => {
        const next = new Map();
        companyOptions.forEach((company) => next.set(company.id, company));
        return next;
    }, [companyOptions]);
    const companyOptionIds = useMemo(
        () => companyOptions.map((company) => company.id),
        [companyOptions],
    );
    const wasteReceiverById = useMemo(() => {
        const next = new Map();
        wasteReceivers.forEach((receiver) => next.set(receiver.id, receiver));
        return next;
    }, [wasteReceivers]);
    const userById = useMemo(() => {
        const next = new Map();
        userOptions.forEach((user) => next.set(user.id, user));
        return next;
    }, [userOptions]);
    const userOptionIds = useMemo(
        () => userOptions.map((user) => user.id),
        [userOptions],
    );
    const isWasteReceiverMode = setupEntityType === SETUP_ENTITY_TYPES.WASTE_RECEIVER;
    const isCompanyMode = setupEntityType === SETUP_ENTITY_TYPES.COMPANY;
    const isUserMode = setupEntityType === SETUP_ENTITY_TYPES.USER;
    const receiverCountLabel =
        wasteReceivers.length === 1
            ? '1 affaldsmodtager'
            : `${wasteReceivers.length} affaldsmodtagere`;
    const busy = loadingReceivers || loadingCompanies || loadingUsers || saving;

    const showMessage = useCallback((message, severity = 'success') => {
        setSnackbar({ message, severity });
    }, []);

    const getCompanyDisplayLabel = useCallback(
        (companyId) => {
            const company = companyById.get(companyId);
            if (!company) return companyId;
            if (company.label === companyId) return companyId;
            return `${company.label} (${companyId})`;
        },
        [companyById],
    );

    const getWasteReceiverDisplayLabel = useCallback(
        (receiverId) => {
            const receiver = wasteReceiverById.get(receiverId);
            return receiver?.name || receiverId || 'en anden affaldsmodtager';
        },
        [wasteReceiverById],
    );

    const getUserDisplayLabel = useCallback(
        (userId) => {
            const user = userById.get(userId);
            if (!user) return userId;
            const secondaryLabel =
                user.secondaryLabel && user.secondaryLabel !== user.label
                    ? user.secondaryLabel
                    : user.id;
            if (secondaryLabel === user.label) return user.label;
            return `${user.label} (${secondaryLabel})`;
        },
        [userById],
    );

    const getCompanyMembershipPermission = useCallback((userId, companyId) => {
        const user = userById.get(userId);
        return normalizeCompanyMembershipPermissionEntry(
            user?.companyMemberships?.[companyId],
        );
    }, [userById]);

    const receiverTab = useWasteReceiverTab({
        isActive: isWasteReceiverMode,
        wasteReceivers,
        companyOptions,
        setCompanyOptions,
        userOptions,
        companyById,
        wasteReceiverById,
        saving,
        setSaving,
        showMessage,
        loadingUsers,
        userLoadError,
        getCompanyDisplayLabel,
        getWasteReceiverDisplayLabel,
    });

    const companiesTab = useCompaniesTab({
        isActive: isCompanyMode,
        companyOptions,
        setCompanyOptions,
        companyById,
        userOptions,
        getCompanyMembershipPermission,
        saving,
        setSaving,
        showMessage,
        loadingUsers,
        userLoadError,
    });

    const usersTab = useUsersTab({
        isActive: isUserMode,
        userOptions,
        wasteReceivers,
        companyOptions,
        wasteReceiverById,
        companyById,
        saving,
        setSaving,
        showMessage,
        loadingCompanies,
        loadingReceivers,
    });

    useEffect(() => {
        setLoadingReceivers(true);
        const unsubscribe = onSnapshot(
            collection(db, WASTE_RECEIVERS_COLLECTION),
            (snap) => {
                const receivers = snap.docs
                    .map(normalizeTransitionWasteReceiverDoc)
                    .sort((a, b) => {
                        const nameCompare = (a.name || a.id).localeCompare(b.name || b.id);
                        return nameCompare || a.id.localeCompare(b.id);
                    });
                setWasteReceivers(receivers);
                setReceiverLoadError('');
                setLoadingReceivers(false);
            },
            (error) => {
                console.error('Error loading waste receivers:', error);
                setReceiverLoadError('Kunne ikke hente affaldsmodtagere.');
                setLoadingReceivers(false);
            },
        );

        return unsubscribe;
    }, []);

    useEffect(() => {
        setLoadingUsers(true);
        const unsubscribe = onSnapshot(
            collection(db, 'users'),
            (snap) => {
                const users = snap.docs
                    .map(normalizeUserDoc)
                    .sort((a, b) => {
                        const labelCompare = a.label.localeCompare(b.label);
                        return labelCompare || a.id.localeCompare(b.id);
                    });
                setUserOptions(users);
                setUserLoadError('');
                setLoadingUsers(false);
            },
            (error) => {
                console.error('Error loading users:', error);
                setUserLoadError('Kunne ikke hente brugerlisten.');
                setLoadingUsers(false);
            },
        );

        return unsubscribe;
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoadingCompanies(true);

        loadCompanyOptions(userData)
            .then((companies) => {
                if (cancelled) return;
                setCompanyOptions(companies);
                setCompanyLoadError('');
            })
            .catch((error) => {
                console.error('Error loading company options:', error);
                if (!cancelled) {
                    setCompanyOptions([]);
                    setCompanyLoadError(
                        'Kunne ikke hente virksomhedslisten. Company-id kan stadig skrives manuelt.',
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingCompanies(false);
            });

        return () => {
            cancelled = true;
        };
    }, [userData]);

    const switchSetupEntityType = (nextType) => {
        if (nextType === setupEntityType || saving) return;

        setSetupEntityType(nextType);
        receiverTab.reset();
        companiesTab.reset();
        usersTab.reset();
    };

    return (
        <div className="setup-page">
            {busy && <LinearProgress className="top-progress" />}

            <div className="setup-toolbar">
                <div className="toolbar-title">
                    <h1>Setup</h1>
                    <div className="setup-mode-toggle" role="tablist" aria-label="Setup type">
                        <Button
                            variant={isWasteReceiverMode ? 'contained' : 'outlined'}
                            onClick={() => switchSetupEntityType(SETUP_ENTITY_TYPES.WASTE_RECEIVER)}
                            disabled={saving}
                        >
                            Affaldsmodtagere
                        </Button>
                        <Button
                            variant={isCompanyMode ? 'contained' : 'outlined'}
                            onClick={() => switchSetupEntityType(SETUP_ENTITY_TYPES.COMPANY)}
                            disabled={saving}
                        >
                            Virksomheder
                        </Button>
                        <Button
                            variant={isUserMode ? 'contained' : 'outlined'}
                            onClick={() => switchSetupEntityType(SETUP_ENTITY_TYPES.USER)}
                            disabled={saving}
                        >
                            Brugere
                        </Button>
                    </div>
                    <div className="toolbar-meta">
                        <Chip
                            size="small"
                            label={receiverCountLabel}
                            className="setup-chip"
                        />
                        <Chip
                            size="small"
                            label={`${companyOptions.length} virksomheder`}
                            className="setup-chip"
                        />
                        <Chip
                            size="small"
                            label={`${userOptions.length} brugere`}
                            className="setup-chip"
                        />
                    </div>
                </div>

                <div className="setup-toolbar-actions">
                    {isWasteReceiverMode ? (
                        <WasteReceiverToolbarActions
                            tab={receiverTab}
                            saving={saving}
                            loadingUsers={loadingUsers}
                            userLoadError={userLoadError}
                        />
                    ) : isCompanyMode ? (
                        <CompaniesToolbarActions
                            tab={companiesTab}
                            saving={saving}
                            loadingUsers={loadingUsers}
                            userLoadError={userLoadError}
                        />
                    ) : (
                        <UsersToolbarActions tab={usersTab} saving={saving} />
                    )}
                </div>
            </div>

            {(receiverLoadError || companyLoadError || userLoadError) && (
                <div className="setup-alerts">
                    {receiverLoadError && <Alert severity="error">{receiverLoadError}</Alert>}
                    {companyLoadError && <Alert severity="warning">{companyLoadError}</Alert>}
                    {userLoadError && <Alert severity="warning">{userLoadError}</Alert>}
                </div>
            )}

            {isUserMode ? (
                <UsersPanel tab={usersTab} userOptions={userOptions} saving={saving} />
            ) : isCompanyMode ? (
                <CompaniesPanel
                    tab={companiesTab}
                    companyOptions={companyOptions}
                    userOptionIds={userOptionIds}
                    userById={userById}
                    loadingCompanies={loadingCompanies}
                    loadingUsers={loadingUsers}
                    userLoadError={userLoadError}
                    saving={saving}
                    getCompanyDisplayLabel={getCompanyDisplayLabel}
                    getUserDisplayLabel={getUserDisplayLabel}
                    getWasteReceiverDisplayLabel={getWasteReceiverDisplayLabel}
                    getCompanyMembershipPermission={getCompanyMembershipPermission}
                />
            ) : (
                <WasteReceiverPanel
                    tab={receiverTab}
                    wasteReceivers={wasteReceivers}
                    companyOptionIds={companyOptionIds}
                    userOptionIds={userOptionIds}
                    companyById={companyById}
                    userById={userById}
                    loadingReceivers={loadingReceivers}
                    loadingCompanies={loadingCompanies}
                    loadingUsers={loadingUsers}
                    companyLoadError={companyLoadError}
                    userLoadError={userLoadError}
                    saving={saving}
                    getCompanyDisplayLabel={getCompanyDisplayLabel}
                    getUserDisplayLabel={getUserDisplayLabel}
                />
            )}

            <Snackbar
                open={Boolean(snackbar)}
                autoHideDuration={3500}
                onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snackbar && (
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
                        {snackbar.message}
                    </Alert>
                )}
            </Snackbar>

            <UserEditorDialog
                tab={usersTab}
                wasteReceivers={wasteReceivers}
                companyOptionIds={companyOptionIds}
                getWasteReceiverDisplayLabel={getWasteReceiverDisplayLabel}
                getCompanyDisplayLabel={getCompanyDisplayLabel}
                loadingReceivers={loadingReceivers}
                loadingCompanies={loadingCompanies}
                saving={saving}
            />

            <CreateUsersDialog
                open={usersTab.provisioningDialog === 'create'}
                onClose={() => usersTab.setProvisioningDialog('')}
                templates={usersTab.welcomeEmailTemplates.templates}
                existingEmails={usersTab.existingEmails}
                wasteReceivers={wasteReceivers}
                companyOptionIds={companyOptionIds}
                getWasteReceiverDisplayLabel={getWasteReceiverDisplayLabel}
                getCompanyDisplayLabel={getCompanyDisplayLabel}
                loadingReceivers={loadingReceivers}
                loadingCompanies={loadingCompanies}
                showMessage={showMessage}
            />

            <WelcomeEmailTemplatesDialog
                open={usersTab.provisioningDialog === 'templates'}
                onClose={() => usersTab.setProvisioningDialog('')}
                templates={usersTab.welcomeEmailTemplates.templates}
                loading={usersTab.welcomeEmailTemplates.loading}
                loadError={usersTab.welcomeEmailTemplates.error}
                showMessage={showMessage}
            />

            <SendWelcomeEmailDialog
                user={usersTab.welcomeEmailUser}
                onClose={() => usersTab.setWelcomeEmailUserId('')}
                templates={usersTab.welcomeEmailTemplates.templates}
                showMessage={showMessage}
            />

            <WasteReceiverDialogs tab={receiverTab} saving={saving} />
        </div>
    );
};

export default SetupPage;
